// temi.ts — i temi di Laughdown. Ogni schermata usa solo queste variabili (CSS custom properties):
// nessun colore scritto a mano nei componenti. Un tema nuovo = una voce qui + il suo stile dei pulsanti
// in stile.css ([data-tema="…"]) + il nome nelle traduzioni (tema.<id>) + l'impostazione app.temi_disponibili.
// Il contrasto di ogni coppia testo/sfondo è controllato da temi.test.ts (WCAG AA).
// Categorie: 3 temi gratuiti; 5 temi Silver; 2 stagionali (gratuiti, si accendono aggiungendoli ad
// app.temi_disponibili quando è stagione). Gratuito = in app.temi_gratuiti; tutti gli altri vogliono Silver o Gold. I caratteri dei temi non gratuiti si scaricano solo
// quando servono (caratteri.ts).

export const ID_TEMI = ["laughdown", "festa", "notte", "caramella", "fumetto", "retro", "pastelli", "essenziale", "tropicale", "zucca", "neve", "oro", "aurora"] as const;
export type IdTema = (typeof ID_TEMI)[number];

export type Aspetto = "chiaro" | "scuro";
export type SceltaAspetto = Aspetto | "automatico";

export interface Colori {
  sfondo: string; // fondo della pagina
  superficie: string; // schede, finestre
  testo: string;
  testoTenue: string;
  primario: string; // pulsante principale
  suPrimario: string; // testo sul pulsante principale
  accento: string; // ombre, dettagli
  secondario: string; // avatar, distintivi
  bordo: string;
  pericolo: string; // "ha riso", errori (sempre accompagnato da una scritta)
  successo: string; // "ha vinto", conferme (sempre accompagnato da una scritta)
}

export interface Tema {
  id: IdTema;
  fontTitolo: string;
  fontTesto: string;
  /** Aspetti disponibili. Un tema solo scuro ignora la scelta "chiaro". */
  aspetti: Partial<Record<Aspetto, Colori>>;
}

// Caratteri di ripiego per gli alfabeti non latini (arabo, devanagari, bengali, thai, CJK…):
// il browser usa il primo carattere della lista che contiene il simbolo.
const RIPIEGO = `"Noto Sans Arabic", system-ui, -apple-system, "Segoe UI", Roboto, "Noto Sans", sans-serif`;

export const TEMI = {
  // v019: l'aspetto unico di Laughdown (notte viola, giallo, personaggi 3D). Solo scuro. I titoli in Nunito nerissimo;
  // il marchio, i timbri e i numeri grandi in Lilita One (classi .ld-marchio, .ld-timbro, .ld-numero in stile.css).
  laughdown: {
    id: "laughdown",
    fontTitolo: `"Nunito", ${RIPIEGO}`,
    fontTesto: `"Nunito", ${RIPIEGO}`,
    aspetti: {
      scuro: {
        sfondo: "#0E0A24",
        superficie: "#17113A",
        testo: "#FFFFFF",
        testoTenue: "#B3A8FF",
        primario: "#FFD60A",
        suPrimario: "#0E0A24",
        accento: "#3DE0FF",
        secondario: "#FF5CC8",
        bordo: "#2F2470",
        pericolo: "#FF6B85",
        successo: "#7CF29A",
      },
    },
  },
  festa: {
    id: "festa",
    fontTitolo: `"Bricolage Grotesque", ${RIPIEGO}`,
    fontTesto: `"DM Sans", ${RIPIEGO}`,
    aspetti: {
      chiaro: {
        sfondo: "#FFD84A",
        superficie: "#FFF6E0",
        testo: "#1E1433",
        testoTenue: "#4A3B66",
        primario: "#1E1433",
        suPrimario: "#FFD84A",
        accento: "#FF5A5F",
        secondario: "#2EC4B6",
        bordo: "#1E1433",
        pericolo: "#C81E2A",
        successo: "#0B6B4F",
      },
      scuro: {
        sfondo: "#1E1433",
        superficie: "#2C2147",
        testo: "#FFF6E0",
        testoTenue: "#D9CFEA",
        primario: "#FFD84A",
        suPrimario: "#1E1433",
        accento: "#FF5A5F",
        secondario: "#2EC4B6",
        bordo: "#FFD84A",
        pericolo: "#FF8A8E",
        successo: "#6EE7B7",
      },
    },
  },
  notte: {
    id: "notte",
    fontTitolo: `"Unbounded", ${RIPIEGO}`,
    fontTesto: `"Space Grotesk", ${RIPIEGO}`,
    aspetti: {
      scuro: {
        sfondo: "#0D0A14",
        superficie: "#1A1426",
        testo: "#F4F1FA",
        testoTenue: "#CFC8E0",
        primario: "#C8FF2E",
        suPrimario: "#0D0A14",
        accento: "#FF3EA5",
        secondario: "#7DF9FF",
        bordo: "#4A4163",
        pericolo: "#FF6FB5",
        successo: "#C8FF2E",
      },
    },
  },
  caramella: {
    id: "caramella",
    fontTitolo: `"Baloo 2", ${RIPIEGO}`,
    fontTesto: `"Nunito", ${RIPIEGO}`,
    aspetti: {
      chiaro: {
        sfondo: "#F7F4FF",
        superficie: "#FFFFFF",
        testo: "#241A4A",
        testoTenue: "#554A7A",
        primario: "#6B4EFF",
        suPrimario: "#FFFFFF",
        accento: "#FFB199",
        secondario: "#4CD6B0",
        bordo: "#E4DCFF",
        pericolo: "#B4233C",
        successo: "#13795B",
      },
      scuro: {
        sfondo: "#17132B",
        superficie: "#231D40",
        testo: "#F1EDFF",
        testoTenue: "#C9C0EE",
        primario: "#9C88FF",
        suPrimario: "#17132B",
        accento: "#FFB199",
        secondario: "#4CD6B0",
        bordo: "#3A3166",
        pericolo: "#FF9AAE",
        successo: "#7FE3C4",
      },
    },
  },
} as Record<IdTema, Tema>;

// --- Temi Silver (v006) ------------------------------------------------------------------------------------------
TEMI.fumetto = {
  id: "fumetto",
  fontTitolo: `"Bangers", ${RIPIEGO}`,
  fontTesto: `"Comic Neue", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#FFF3C4",
      superficie: "#FFFFFF",
      testo: "#111111",
      testoTenue: "#3D3D3D",
      primario: "#D62828",
      suPrimario: "#FFFFFF",
      accento: "#1D4ED8",
      secondario: "#FACC15",
      bordo: "#111111",
      pericolo: "#B91C1C",
      successo: "#15803D",
    },
    scuro: {
      sfondo: "#14161F",
      superficie: "#1F2230",
      testo: "#FFF8E1",
      testoTenue: "#D7D2C0",
      primario: "#FFD23F",
      suPrimario: "#14161F",
      accento: "#FF4D4D",
      secondario: "#4D8BFF",
      bordo: "#FFF8E1",
      pericolo: "#FF8080",
      successo: "#6EE7A0",
    },
  },
};
TEMI.retro = {
  id: "retro",
  fontTitolo: `"Shrikhand", ${RIPIEGO}`,
  fontTesto: `"DM Sans", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#F6E7C8",
      superficie: "#FFF6E3",
      testo: "#3B1F0E",
      testoTenue: "#6B4226",
      primario: "#B23A0B",
      suPrimario: "#FFF6E3",
      accento: "#E9A23B",
      secondario: "#2F6F5E",
      bordo: "#3B1F0E",
      pericolo: "#A11D2B",
      successo: "#2F6B3A",
    },
    scuro: {
      sfondo: "#2A1810",
      superficie: "#3A2418",
      testo: "#F8E7C9",
      testoTenue: "#E0C8A4",
      primario: "#F2A541",
      suPrimario: "#2A1810",
      accento: "#E06C3C",
      secondario: "#7BB8A4",
      bordo: "#F2A541",
      pericolo: "#FF9A8A",
      successo: "#A8E0A0",
    },
  },
};
TEMI.pastelli = {
  id: "pastelli",
  fontTitolo: `"Patrick Hand", ${RIPIEGO}`,
  fontTesto: `"Nunito", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#FBF7EF",
      superficie: "#FFFFFF",
      testo: "#2E2A40",
      testoTenue: "#5A556E",
      primario: "#3E6FB0",
      suPrimario: "#FFFFFF",
      accento: "#F2A7C3",
      secondario: "#A8D8B9",
      bordo: "#2E2A40",
      pericolo: "#B3264F",
      successo: "#2D7A4F",
    },
  },
};
TEMI.essenziale = {
  id: "essenziale",
  fontTitolo: `"Space Grotesk", ${RIPIEGO}`,
  fontTesto: `"DM Sans", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#FFFFFF",
      superficie: "#F4F4F4",
      testo: "#000000",
      testoTenue: "#4A4A4A",
      primario: "#000000",
      suPrimario: "#FFFFFF",
      accento: "#000000",
      secondario: "#8A8A8A",
      bordo: "#000000",
      pericolo: "#B00020",
      successo: "#006B3C",
    },
    scuro: {
      sfondo: "#000000",
      superficie: "#141414",
      testo: "#FFFFFF",
      testoTenue: "#BDBDBD",
      primario: "#FFFFFF",
      suPrimario: "#000000",
      accento: "#FFFFFF",
      secondario: "#777777",
      bordo: "#FFFFFF",
      pericolo: "#FF7A85",
      successo: "#5FD39A",
    },
  },
};
TEMI.tropicale = {
  id: "tropicale",
  fontTitolo: `"Fredoka", ${RIPIEGO}`,
  fontTesto: `"Nunito", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#E6FBF4",
      superficie: "#FFFFFF",
      testo: "#0B3B33",
      testoTenue: "#2F5F56",
      primario: "#0F766E",
      suPrimario: "#FFFFFF",
      accento: "#FF7A59",
      secondario: "#FFC93C",
      bordo: "#0B3B33",
      pericolo: "#C2185B",
      successo: "#15803D",
    },
    scuro: {
      sfondo: "#052A26",
      superficie: "#0A3A34",
      testo: "#E9FFF8",
      testoTenue: "#B5E3D6",
      primario: "#FFC93C",
      suPrimario: "#052A26",
      accento: "#FF7A59",
      secondario: "#4FD1C5",
      bordo: "#FFC93C",
      pericolo: "#FF9BB5",
      successo: "#7EE8A8",
    },
  },
};

// --- Temi stagionali (gratuiti, accesi dal pannello quando è stagione) ---------------------------------------------
TEMI.zucca = {
  id: "zucca",
  fontTitolo: `"Unbounded", ${RIPIEGO}`,
  fontTesto: `"Space Grotesk", ${RIPIEGO}`,
  aspetti: {
    scuro: {
      sfondo: "#17111F",
      superficie: "#241A30",
      testo: "#FFF4E6",
      testoTenue: "#D8C8B8",
      primario: "#FF7A1A",
      suPrimario: "#17111F",
      accento: "#8BE04E",
      secondario: "#B07CFF",
      bordo: "#FF7A1A",
      pericolo: "#FF8FA3",
      successo: "#8BE04E",
    },
  },
};
TEMI.neve = {
  id: "neve",
  fontTitolo: `"Baloo 2", ${RIPIEGO}`,
  fontTesto: `"Nunito", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#EEF6FF",
      superficie: "#FFFFFF",
      testo: "#0E2A47",
      testoTenue: "#3C5A78",
      primario: "#C62828",
      suPrimario: "#FFFFFF",
      accento: "#2E7D32",
      secondario: "#90CAF9",
      bordo: "#0E2A47",
      pericolo: "#B71C1C",
      successo: "#1B5E20",
    },
    scuro: {
      sfondo: "#0B1B2E",
      superficie: "#13283F",
      testo: "#F2F8FF",
      testoTenue: "#C3D4E6",
      primario: "#FF6B6B",
      suPrimario: "#0B1B2E",
      accento: "#7BD389",
      secondario: "#90CAF9",
      bordo: "#C3D4E6",
      pericolo: "#FF9A9A",
      successo: "#7BD389",
    },
  },
};

TEMI.oro = {
  // Gold: nero e oro, sobrio ed elegante (pulsanti con il bordo dorato).
  id: "oro",
  fontTitolo: `"Bricolage Grotesque", ${RIPIEGO}`,
  fontTesto: `"DM Sans", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#F7EFD9",
      superficie: "#FFFBF0",
      testo: "#1A1408",
      testoTenue: "#5A4A2A",
      primario: "#1A1408",
      suPrimario: "#E9C46A",
      accento: "#B8860B",
      secondario: "#7A5C1E",
      bordo: "#1A1408",
      pericolo: "#B3261E",
      successo: "#1B4A1F",
    },
    scuro: {
      sfondo: "#0E0B07",
      superficie: "#1C160D",
      testo: "#F6E7C1",
      testoTenue: "#CDB98A",
      primario: "#E9C46A",
      suPrimario: "#0E0B07",
      accento: "#F4D58D",
      secondario: "#B8860B",
      bordo: "#E9C46A",
      pericolo: "#FF8A80",
      successo: "#9BD39B",
    },
  },
};
TEMI.aurora = {
  // Gold: aurora boreale, verde acqua e viola che brillano sul blu notte.
  id: "aurora",
  fontTitolo: `"Bricolage Grotesque", ${RIPIEGO}`,
  fontTesto: `"DM Sans", ${RIPIEGO}`,
  aspetti: {
    chiaro: {
      sfondo: "#E8F7F3",
      superficie: "#FFFFFF",
      testo: "#0B1E2D",
      testoTenue: "#33515F",
      primario: "#0B1E2D",
      suPrimario: "#7CF0C5",
      accento: "#7B61FF",
      secondario: "#18A999",
      bordo: "#0B1E2D",
      pericolo: "#B3261E",
      successo: "#064432",
    },
    scuro: {
      sfondo: "#071421",
      superficie: "#0E2233",
      testo: "#E8FFF7",
      testoTenue: "#A9D6CC",
      primario: "#5CF2B8",
      suPrimario: "#071421",
      accento: "#B49CFF",
      secondario: "#3FC1C9",
      bordo: "#5CF2B8",
      pericolo: "#FF9A9A",
      successo: "#7BE3A3",
    },
  },
};

export const TEMA_RIPIEGO: IdTema = "laughdown";

/** Temi gratuiti (i soli che possono essere il tema predefinito, oltre agli stagionali). */
export const TEMI_GRATUITI: readonly IdTema[] = ["laughdown", "festa", "notte", "caramella", "zucca", "neve"];
/** Temi Silver (valori di partenza delle impostazioni; il pannello decide con app.temi_gratuiti). */
export const TEMI_SILVER_PREDEFINITI: readonly IdTema[] = ["fumetto", "retro", "pastelli", "essenziale", "tropicale"];

/** Temi riservati a Gold (valore di partenza di app.temi_gold). */
export const TEMI_GOLD_PREDEFINITI: readonly IdTema[] = ["oro", "aurora"];

/** Il tema si può usare con questo piano? (`gratuiti` = app.temi_gratuiti, `soloGold` = app.temi_gold) */
export function temaUsabile(id: IdTema, piano: "base" | "silver" | "gold", gratuiti: readonly string[], soloGold: readonly string[] = TEMI_GOLD_PREDEFINITI): boolean {
  if (soloGold.includes(id)) return piano === "gold";
  return piano !== "base" || gratuiti.includes(id);
}

/** Che piano serve per un tema bloccato (per l'etichetta nel pennello). */
export function pianoPerTema(id: IdTema, soloGold: readonly string[] = TEMI_GOLD_PREDEFINITI): "silver" | "gold" {
  return soloGold.includes(id) ? "gold" : "silver";
}

export function eIdTema(v: unknown): v is IdTema {
  return typeof v === "string" && (ID_TEMI as readonly string[]).includes(v);
}

/** Sceglie i colori: rispetta la scelta dell'utente se il tema la offre, altrimenti l'aspetto che ha. */
export function coloriDelTema(tema: Tema, scelta: SceltaAspetto, sistemaScuro: boolean): { aspetto: Aspetto; colori: Colori } {
  const voluto: Aspetto = scelta === "automatico" ? (sistemaScuro ? "scuro" : "chiaro") : scelta;
  const colori = tema.aspetti[voluto];
  if (colori) return { aspetto: voluto, colori };
  const altro: Aspetto = voluto === "chiaro" ? "scuro" : "chiaro";
  return { aspetto: altro, colori: tema.aspetti[altro]! };
}

export function haEntrambiGliAspetti(tema: Tema): boolean {
  return Boolean(tema.aspetti.chiaro && tema.aspetti.scuro);
}
